var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from 'react';
const VersionCheck = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [currentVersion, setCurrentVersion] = useState(null);
    const checkVersion = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield fetch('/version');
            if (!response.ok) {
                console.error('Failed to fetch version');
                return;
            }
            const version = (yield response.text()).trim();
            // Validate that we got an actual version string, not HTML
            if (!version || version.startsWith('<!') || version.startsWith('<html')) {
                console.error('Invalid version response - received HTML instead of version string');
                return;
            }
            setCurrentVersion(version);
            // Check if version has changed
            const savedVersion = localStorage.getItem('alphafi_last_version');
            const dismissedDate = localStorage.getItem('alphafi_version_dismissed_date');
            const today = new Date().toDateString();
            // Check if version has changed and we haven't dismissed it today
            const hasVersionChanged = savedVersion && savedVersion !== version;
            const wasDismissedToday = dismissedDate === today;
            if (hasVersionChanged && !wasDismissedToday) {
                setShowBanner(true);
                localStorage.setItem('alphafi_last_version', version);
            }
            else if (!savedVersion) {
                localStorage.setItem('alphafi_last_version', version);
            }
        }
        catch (error) {
            console.error('Error checking version:', error);
        }
    }), []);
    // Check version on mount
    useEffect(() => {
        checkVersion();
    }, [checkVersion]);
    // Set up periodic version checks
    useEffect(() => {
        if (!showBanner) {
            const intervalId = setInterval(() => {
                if (document.visibilityState === 'visible') {
                    checkVersion();
                }
            }, 5 * 60 * 1000); // Check every 5 minutes
            return () => clearInterval(intervalId);
        }
    }, [checkVersion, showBanner]);
    // Listen for visibility changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && !showBanner) {
                checkVersion();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [checkVersion, showBanner]);
    // Listen for route changes
    useEffect(() => {
        const handleRouteChange = () => {
            if (!showBanner) {
                checkVersion();
            }
        };
        window.addEventListener('popstate', handleRouteChange);
        return () => window.removeEventListener('popstate', handleRouteChange);
    }, [checkVersion, showBanner]);
    // Listen for transaction events
    useEffect(() => {
        const handleTransactionEvent = () => {
            if (!showBanner) {
                checkVersion();
            }
        };
        window.addEventListener('transactionSuccess', handleTransactionEvent);
        window.addEventListener('transactionError', handleTransactionEvent);
        return () => {
            window.removeEventListener('transactionSuccess', handleTransactionEvent);
            window.removeEventListener('transactionError', handleTransactionEvent);
        };
    }, [checkVersion, showBanner]);
    const handleRefresh = () => {
        if (currentVersion) {
            localStorage.setItem('alphafi_last_version', currentVersion);
        }
        window.location.reload();
    };
    const handleDismiss = () => {
        const today = new Date().toDateString();
        localStorage.setItem('alphafi_version_dismissed_date', today);
        setShowBanner(false);
    };
    if (!showBanner) {
        return null;
    }
    return (_jsx("div", { className: "fixed top-0 left-0 right-0 z-50 bg-[#282A2F] text-white animate-slideDown", children: _jsx("div", { className: "max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8", children: _jsxs("div", { className: "flex items-center justify-between flex-wrap", children: [_jsx("div", { className: "flex-1 flex items-center", children: _jsx("p", { className: "font-medium text-sm sm:text-base", children: "A new version of AlphaFi is available! Refresh to get the latest updates." }) }), _jsxs("div", { className: "flex items-center space-x-4 mt-2 sm:mt-0", children: [_jsx("button", { onClick: handleRefresh, className: "bg-white text-[#282A2F] px-4 py-1.5 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors", children: "Refresh Now" }), _jsx("button", { onClick: handleDismiss, className: "text-white hover:text-gray-300 transition-colors", "aria-label": "Dismiss", children: _jsx("svg", { className: "h-5 w-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) })] })] }) }) }));
};
export default VersionCheck;
